import moment from 'moment';
import { CONSTANTS } from 'src/components/Constants';
import { VARIANT_ORIGIN_OPTIONS } from './sections/clinical-notes-section/forms/formService';

// ─────────────────────────────────────────────────────────────────────────────
// Record Modification History — frontend formatter registry (Phase 1)
//
// The backend read model tags each change with a `type` (from the field
// registry sourced from import-schema.json + reviewed clinical map). Here we map
// that type → a display string + whether to render mono-spaced (HGVS). Enum
// value→label maps reuse the app's existing option lists; no field labels live
// here (those come from the backend `label`).
// ─────────────────────────────────────────────────────────────────────────────

// zygosity / carrier-status value → label (reuse the existing option list)
const ZYGOSITY = {};
(CONSTANTS.carrierStatuses || []).forEach((o) => {
  ZYGOSITY[o.value] = o.label;
});

// variant origin value → label, as the Test form offers it ('unknown' reads
// "Not stated", never "Unknown")
const ORIGIN = {};
VARIANT_ORIGIN_OPTIONS.forEach((o) => {
  ORIGIN[o.value] = o.label;
});

const humanise = (v) =>
  String(v)
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

/** HGVS values render mono-spaced. */
export const isMono = (type) => type === 'hgvs';

/**
 * Format a raw stored value for display given its renderer `type`.
 * Returns null for an "empty" value so callers can render their own
 * placeholder — never a literal "empty" string that could collide with real
 * data. Objects/arrays should never reach here (the backend only emits scalar
 * changes) but are guarded to null defensively rather than rendered as
 * "[object Object]".
 */
export function formatValue(type, v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'object') return null;
  switch (type) {
    case 'enum:zygosity':
      return ZYGOSITY[v] || humanise(v);
    case 'enum:inheritance':
      return humanise(v);
    case 'enum:origin':
      return ORIGIN[v] || humanise(v);
    case 'enum:acmg':
      return String(v); // already stored as a label, e.g. "Pathogenic"
    case 'booleanAlias': {
      const s = String(v).toLowerCase();
      if (s === 'true') return 'Alive';
      if (s === 'false' || s === 'no') return 'Deceased';
      return humanise(v);
    }
    case 'boolean':
      return v === true || v === 'true' ? 'Yes' : 'No';
    case 'date':
    case 'partialDate': {
      const m = moment(v);
      return m.isValid() ? m.format('DD/MM/YYYY') : String(v);
    }
    case 'number':
      return String(v);
    case 'count': {
      // Array fields are summarised to an element count, never their content.
      const n = Number(v);
      return `${n} ${n === 1 ? 'item' : 'items'}`;
    }
    case 'hgvs':
    case 'text':
    default:
      return String(v);
  }
}
